import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ASYNC_JOB_POLL_INTERVAL_MS } from '@core/constants/common';
import type { AsyncJobSummary } from '@core/types/api/AsyncJobSummary/AsyncJobSummary';
import Enums from '@core/types/api/Enums';
import { NIL_UUID } from '@core/utils/uuid';
import type { QueryRouteBarrelKeys } from '@web/utils/ApiClient';

import { useSlabQuery } from '../../hooks/useSlabQuery';
import {
  ASYNC_JOB_STATUS_JOB_TYPES,
  type AsyncJobStatusConfig,
  asyncJobStatusConfigFor,
} from './AsyncJobStatusConfig';

export type UseAsyncJobStatusReturn = {
  job: AsyncJobSummary | null;
  config: AsyncJobStatusConfig | null;
  isCurrentlyActive: boolean;
  dismiss: () => void;
  trackJob: (jobId: string, jobType: Enums.AsyncJobType) => void;
};

const isInProgress = (job: AsyncJobSummary): boolean =>
  job.status === Enums.AsyncJobStatus.Pending || job.status === Enums.AsyncJobStatus.Processing;

/**
 * Discovers and polls the current user's latest configured async job.
 * Jobs started in this browser are tracked by ID; if there's no tracked ID,
 * the hook falls back to discovering the latest configured job type. Jobs in
 * progress are tracked to ensure that previous (already completed/failed) jobs
 * do not render the status tracker for no reason.
 */
export const useAsyncJobStatus = (): UseAsyncJobStatusReturn => {
  const queryClient = useQueryClient();
  const [trackedJob, setTrackedJob] = useState<{ id: string } | null>(null);
  const [dismissedJobIds, setDismissedJobIds] = useState<Set<string>>(new Set());
  const seenInProgressRef = useRef<Set<string>>(new Set());
  const terminalInvalidatedJobIdsRef = useRef<Set<string>>(new Set());

  const { data } = useSlabQuery(
    'GET async job summaries',
    {
      queryParams: {
        perPage: 1,
        page: 0,
        sortBy: {
          name: 'createdAt',
          direction: Enums.SortDirection.Descending,
        },
        filterBy:
          trackedJob === null
            ? [
                {
                  name: 'jobType',
                  operation: Enums.FilterOperation.In,
                  values: ASYNC_JOB_STATUS_JOB_TYPES,
                },
              ]
            : [
                {
                  name: 'id',
                  operation: Enums.FilterOperation.Equals,
                  values: [trackedJob.id],
                },
              ],
      },
    },
    {
      refetchInterval: (query): number | false => {
        const latestJob = query.state.data?.items?.[0];
        return latestJob !== undefined && isInProgress(latestJob)
          ? ASYNC_JOB_POLL_INTERVAL_MS
          : false;
      },
    },
  );

  const latestJob = data?.items?.[0] ?? null;
  const latestConfig = latestJob === null ? undefined : asyncJobStatusConfigFor(latestJob.jobType);
  const trackedJobId = trackedJob?.id;

  useEffect(() => {
    if (
      latestJob === null ||
      latestConfig === undefined ||
      isInProgress(latestJob) ||
      !seenInProgressRef.current.has(latestJob.id)
    ) {
      return;
    }

    const terminalInvalidationRouteKeys = latestConfig.terminalInvalidationRouteKeys ?? [];
    if (
      terminalInvalidationRouteKeys.length === 0 ||
      terminalInvalidatedJobIdsRef.current.has(latestJob.id)
    ) {
      return;
    }

    terminalInvalidatedJobIdsRef.current.add(latestJob.id);
    queryClient.invalidateQueries({
      predicate: (query) =>
        terminalInvalidationRouteKeys.includes(query.queryKey[1] as QueryRouteBarrelKeys),
    });
  }, [latestConfig, latestJob, queryClient, trackedJobId]);

  if (latestJob !== null && isInProgress(latestJob)) {
    seenInProgressRef.current.add(latestJob.id);
  }

  // Show the job if:
  //   1. It's currently in-progress (always show), OR
  //   2. It's completed/failed AND we saw it in-progress during this session
  // AND the user hasn't dismissed it.
  const isVisible =
    latestJob !== null &&
    latestConfig !== undefined &&
    !dismissedJobIds.has(latestJob.id) &&
    (isInProgress(latestJob) || seenInProgressRef.current.has(latestJob.id));

  const trackJob = useCallback((jobId: string, jobType: Enums.AsyncJobType): void => {
    if (jobId === '' || jobId === NIL_UUID || asyncJobStatusConfigFor(jobType) === undefined) {
      return;
    }

    seenInProgressRef.current.add(jobId);
    setDismissedJobIds((previous) => {
      const next = new Set(previous);
      next.delete(jobId);
      return next;
    });
    setTrackedJob({ id: jobId });
  }, []);

  const dismiss = useCallback((): void => {
    if (latestJob === null) {
      return;
    }

    setDismissedJobIds((previous) => new Set(previous).add(latestJob.id));
    setTrackedJob((previous) => (previous?.id === latestJob.id ? null : previous));
  }, [latestJob]);

  return {
    job: isVisible ? latestJob : null,
    config: isVisible ? latestConfig : null,
    isCurrentlyActive: latestJob ? isInProgress(latestJob) : false,
    dismiss,
    trackJob,
  };
};
